/**
 * Funções puras extraídas de `useAuth.tsx` (item 2.14 do plano de dívida técnica).
 *
 * O objetivo é isolar decisões de sessão/permissões/normalização do componente
 * `AuthProvider`, tornando-as testáveis sem React nem cliente Supabase.
 */

export type SupabaseErrorLike = {
  code?: string;
  message?: string;
};

export type ProfileRowLike = {
  full_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  phone?: string | null;
  email?: string | null;
  role?: string | null;
};

export type ProfessionalLike = {
  id: string | null;
  user_id?: string | null;
  full_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  email?: string | null;
  role?: string | null;
  user_type?: string | null;
};

export type AuthSnapshot = {
  isMaster: boolean;
  profile: { full_name: string; phone: string | null } | null;
  professional: { id: string; nickname: string | null; name: string; user_type: string } | null;
};

export type CachedAuthSnapshot = AuthSnapshot & { userId: string; cachedAt: number };

export const UUID_V4ISH_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isUuidLike = (value: string | null | undefined) =>
  !!value && UUID_V4ISH_REGEX.test(value);

/** Detecta o erro do PostgREST/Postgres de coluna `user_id` inexistente. */
export const shouldFallbackToProfileId = (error: SupabaseErrorLike | null | undefined) => {
  if (!error) return false;
  if (error.code === "42703") return true;

  const normalizedMessage = error.message?.toLowerCase() ?? "";
  return normalizedMessage.includes("user_id") && normalizedMessage.includes("does not exist");
};

/** Detecta erro de coluna específica inexistente (schema legado). */
export const isMissingColumnError = (
  error: SupabaseErrorLike | null | undefined,
  columnName: string,
) => {
  if (!error) return false;

  const normalizedMessage = error.message?.toLowerCase() ?? "";
  return (
    normalizedMessage.includes(columnName.toLowerCase()) &&
    normalizedMessage.includes("does not exist")
  );
};

/** Decide se a query canônica (com colunas novas) precisa do fallback legado. */
export const needsLegacyProfessionalFallback = (error: SupabaseErrorLike | null | undefined) =>
  isMissingColumnError(error, "full_name") || isMissingColumnError(error, "role");

/** Melhor nome de exibição a partir de uma linha de `profiles`. */
export const resolveDisplayNameFromProfileRow = (row: ProfileRowLike | null | undefined) =>
  row?.nickname?.trim() ||
  row?.full_name?.trim() ||
  row?.name?.trim() ||
  row?.email?.trim() ||
  null;

/** Papel efetivo a ser gravado no perfil durante o sync pós-login. */
export const computeEffectiveProfileRole = (options: {
  hasMasterAccess: boolean;
  currentProfileRole?: string | null;
}) => {
  if (options.hasMasterAccess) return "master";
  const role = options.currentProfileRole;
  return role && role !== "user" ? role : "professional";
};

/** Evita upsert redundante quando o perfil já está consistente. */
export const shouldSkipProfileUpsert = (options: {
  hasProfileRow: boolean;
  existingRole?: string | null;
  effectiveRole: string;
  existingEmail: string;
  userEmail: string;
  existingFullName?: string | null;
}) =>
  options.hasProfileRow &&
  options.existingRole === options.effectiveRole &&
  options.existingEmail === options.userEmail &&
  !!options.existingFullName;

export type ProfileUpsertPayload = {
  user_id: string;
  email: string;
  role: string;
  updated_at: string;
  name?: string;
};

export const buildProfileUpsertPayload = (options: {
  userId: string;
  email?: string | null;
  role: string;
  metadataName?: string | null;
  existingFullName?: string | null;
  now?: Date;
}): ProfileUpsertPayload => {
  const payload: ProfileUpsertPayload = {
    user_id: options.userId,
    email: options.email ?? "",
    role: options.role,
    updated_at: (options.now ?? new Date()).toISOString(),
  };

  if (!options.existingFullName) {
    payload.name = options.metadataName ?? "";
  }

  return payload;
};

/** Monta o snapshot de auth a partir das linhas já buscadas. */
export const buildAuthSnapshot = (options: {
  profileRow: ProfileRowLike | null;
  professionalData: ProfessionalLike | null;
  normalizedEmail: string | null;
  isMaster: boolean;
  normalizedProfessionalType: string;
}): AuthSnapshot => {
  const { profileRow, professionalData, normalizedEmail } = options;

  return {
    isMaster: options.isMaster,
    profile: profileRow
      ? {
          full_name: profileRow.full_name ?? profileRow.name ?? "",
          phone: profileRow.phone ?? null,
        }
      : null,
    professional: professionalData?.id
      ? {
          id: professionalData.id,
          nickname: professionalData.nickname ?? null,
          name:
            professionalData.full_name?.trim() ||
            professionalData.name?.trim() ||
            profileRow?.full_name?.trim() ||
            normalizedEmail?.split("@")[0] ||
            "Profissional",
          user_type: options.normalizedProfessionalType,
        }
      : null,
  };
};

/** Snapshot "vazio" usado quando não há sessão ou o fetch falhou sem cache. */
export const emptyAuthSnapshot = (): AuthSnapshot => ({
  isMaster: false,
  profile: null,
  professional: null,
});

/**
 * Senhas candidatas para o login: a original e, quando diferente, a versão
 * sem espaços nas bordas (compatibilidade com senhas legadas coladas).
 */
export const buildSignInPasswordCandidates = (password: string) => {
  const trimmed = password.trim();
  return trimmed && trimmed !== password ? [password, trimmed] : [password];
};

/** Valida um snapshot vindo do localStorage para o usuário atual. */
export const parseCachedAuthSnapshot = (
  raw: string | null,
  userId: string,
): AuthSnapshot | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedAuthSnapshot;
    if (!parsed || parsed.userId !== userId) return null;
    return {
      isMaster: !!parsed.isMaster,
      profile: parsed.profile ?? null,
      professional: parsed.professional ?? null,
    };
  } catch {
    return null;
  }
};

/** Nome de fallback do usuário autenticado quando o perfil não resolve. */
export const resolveFallbackUserName = (user: {
  email?: string | null;
  user_metadata?: { nickname?: string | null; full_name?: string | null } | null;
}) =>
  user.user_metadata?.nickname ||
  user.user_metadata?.full_name ||
  user.email ||
  "Usuário";
